import React, { useState, useEffect } from "react";
import { ChevronDown, X } from "lucide-react";

const MultiSelectDropdown = ({ label, options = [], value = [], onChange, placeholder = "Select options", isViewOnly = false, anchorToParent = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState(value || []);
  const [tempSelected, setTempSelected] = useState([...selected]);

  useEffect(() => {
    if (!isOpen) setSearchTerm("");
  }, [isOpen]);

  useEffect(() => {
    setSelected(value || []);
    setTempSelected([...value || []]);
  }, [value]);

  const toggleSelection = (option) => {
    if (isViewOnly) return;
    const exists = tempSelected.some((item) => item.id === option.id);
    if (exists) {
      setTempSelected(tempSelected.filter((item) => item.id !== option.id));
    } else {
      setTempSelected([...tempSelected, option]);
    }
  };

  const applySelection = () => {
    setSelected(tempSelected);
    onChange(tempSelected);
    setIsOpen(false);
  };

  const cancelSelection = () => {
    setTempSelected([...selected]); // Restore previous selection
    setIsOpen(false);
  };

  const resetSelection = () => {
    setSelected([]);
    setTempSelected([]);
    setSearchTerm("");
    onChange([]);
    setIsOpen(false);
  };

  const filteredOptions = options.filter((option) =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // mobile-friendly sheet when open on small screens
  return (
    <div className="relative w-full">
      <label className="mb-2 mr-1 text-xs font-medium">{label}</label>

      <div
        className={`border p-2 bg-white rounded-md flex items-center justify-between ${isViewOnly ? "bg-gray-200 cursor-not-allowed" : "cursor-pointer"}`}
        onClick={() => !isViewOnly && setIsOpen((s) => !s)}
        role="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <span className="truncate">
          {selected.length > 0
            ? selected
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((item) => item.name)
                .join(", ")
            : placeholder}
        </span>
        {!isViewOnly && <ChevronDown className="w-5 h-5 text-gray-500" />}
      </div>

      {isOpen && !isViewOnly && (
        <>
          {/* overlay only when using viewport-fixed sheet; if anchoring to parent don't render full-screen overlay */}
          {!anchorToParent && (
            <div
              className="fixed inset-0 z-40 bg-black/40 sm:hidden"
              onClick={() => setIsOpen(false)}
              aria-hidden="true"
            />
          )}

          <div
            // when anchorToParent: position absolute inside parent; otherwise fixed bottom-sheet
            className={
              anchorToParent
                ? "absolute left-0 right-0 bottom-0 z-50 sm:relative sm:mt-1 sm:w-full sm:rounded-lg sm:shadow-lg"
                : "fixed inset-x-0 bottom-0 z-50 sm:relative sm:mt-1 sm:w-full sm:rounded-lg sm:shadow-lg"
            }
            role="dialog"
            aria-modal="true"
            style={anchorToParent ? { transform: "translateY(0)" } : undefined}
          >
            <div className="bg-white rounded-t-lg sm:rounded-lg shadow-lg max-h-[70vh] overflow-auto" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
              <div className="flex items-center justify-between p-3 border-b">
                <div className="flex items-center gap-2">
                  <button className="sm:hidden p-1" onClick={() => setIsOpen(false)} aria-label="Close">
                    <X size={18} />
                  </button>
                  <span className="font-medium text-sm">Select</span>
                </div>
                <div className="hidden sm:flex items-center gap-2">
                  <button className="px-3 py-1 rounded bg-gray-100 text-sm" onClick={resetSelection}>Reset</button>
                  <button className="px-3 py-1 rounded bg-primary text-white" onClick={applySelection}>Apply</button>
                </div>
              </div>

              <div className="p-3">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm">🔍</span>
                  <input
                    type="text"
                    placeholder="Search..."
                    className="w-full p-2 border rounded text-sm"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="space-y-1 max-h-[36vh] overflow-y-auto">
                  {filteredOptions.map((option) => {
                    const selectedFlag = tempSelected.some((item) => item.id === option.id);
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => toggleSelection(option)}
                        className={`w-full text-left p-2 rounded ${selectedFlag ? "bg-blue-100" : "hover:bg-gray-50"}`}
                      >
                        <div className="flex justify-between items-center">
                          <span className="truncate">{option.name}</span>
                          {selectedFlag && <span className="text-sm text-blue-700">✓</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* mobile action bar */}
                <div className="sm:hidden fixed left-0 right-0 bottom-0 z-50 bg-white border-t p-3 flex gap-2" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
                  <button className="flex-1 rounded-md border border-stroke px-3 py-2 text-sm" onClick={resetSelection}>Reset</button>
                  <button className="flex-1 rounded-md bg-primary text-white px-3 py-2 text-sm" onClick={applySelection}>Apply</button>
                </div>

                {/* desktop inline actions */}
                <div className="hidden sm:flex justify-between items-center mt-3">
                  <div />
                  <div className="flex gap-2">
                    <button className="px-3 py-1 rounded bg-gray-100 text-sm" onClick={resetSelection}>Reset</button>
                    <button className="px-3 py-1 rounded bg-primary text-white" onClick={applySelection}>Apply</button>
                    <button className="px-3 py-1 rounded bg-white border" onClick={cancelSelection}>Cancel</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
